import validator from 'validator';
import { z } from 'zod';

import { stringToArray, stringToArrayQuote } from '../../../utilities';

export type ViolationType = `S${number}`;

const isViolation = (value: string): value is ViolationType => {
  return /^S\d+$/.test(value);
};

export const validateViolations = (string?: string) => {
  const arr = stringToArray(string || '');
  return arr.every(isViolation) ? false : 'Invalid violation structure';
};

const isValidDomain = (
  url: string,
  protocols: string[] = ['https']
): boolean => {
  return validator.isURL(url, {
    protocols,
    require_protocol: true,
    require_valid_protocol: true,
    allow_fragments: false,
    allow_query_components: false,
  });
};

export const validateDomains =
  (protocols: string[] = ['https']) =>
  (string?: string): string | false => {
    const arr = stringToArrayQuote(string || '');
    return arr.every(domain => isValidDomain(domain, protocols))
      ? false
      : 'Invalid violation structure';
  };

export const validateMinValue =
  (min: number) =>
  (value: number | string | null | undefined): string | false => {
    if (value === null || value === undefined || value === '') {
      return false;
    }

    const schema = z
      .number({ invalid_type_error: 'Value must be a number' })
      .min(min, `Value must be ${min} or higher`);

    const parsed = schema.safeParse(Number(value));
    if (parsed.success) {
      return false;
    }

    return parsed.error.issues[0]?.message ?? `Value must be ${min} or higher`;
  };

type NumericValue = number | string | null | undefined;

const toNumber = (value: NumericValue): number | undefined => {
  if (value === null || value === undefined || value === '') return undefined;
  const n = Number(value);
  return Number.isNaN(n) ? undefined : n;
};

const formatRangeMessage = (min: number, max: number, unit?: string) =>
  `Must be between ${min} and ${max}${unit ? ` ${unit}` : ''}`;

export const makeRangeValidator =
  (min: number, max: number, unit?: string) =>
  (value: NumericValue): string | undefined => {
    const n = toNumber(value);
    if (n === undefined) return undefined;

    const message = formatRangeMessage(min, max, unit);
    const result = z
      .number({ invalid_type_error: 'Must be a number' })
      .min(min, message)
      .max(max, message)
      .safeParse(n);
    if (!result.success) return result.error.issues[0]?.message;
    return undefined;
  };

export const makeIntRangeValidator =
  (min: number, max: number, unit?: string) =>
  (value: NumericValue): string | undefined => {
    const n = toNumber(value);
    if (n === undefined) return undefined;

    if (!Number.isInteger(n)) return 'Must be a whole number';

    const message = formatRangeMessage(min, max, unit);
    const result = z
      .number({ invalid_type_error: 'Must be a number' })
      .int('Must be a whole number')
      .min(min, message)
      .max(max, message)
      .safeParse(n);
    if (!result.success) return result.error.issues[0]?.message;
    return undefined;
  };

export const validateChunkValue =
  (options: {
    min: number;
    max?: number;
    otherFieldName: string;
    errorMessage: string;
    comparison?: 'equal' | 'lessThan' | 'greaterThan';
  }) =>
  (
    value: number | string | null | undefined,
    values: Record<string, any>
  ): string | false => {
    const { min, max, otherFieldName, errorMessage, comparison = 'equal' } = options;

    if (value === null || value === undefined || value === '') return false;

    let schema = z
      .number({ invalid_type_error: 'Value must be a number' })
      .min(min, `Value must be ${min} or higher`);

    if (max !== undefined) {
      schema = schema.max(max, `Value must be ${max} or lower`);
    }

    const parsed = schema.safeParse(Number(value));
    if (!parsed.success) {
      return parsed.error.issues[0]?.message ?? `Value must be ${min} or higher`;
    }

    const otherValue = values?.[otherFieldName];
    if (otherValue !== undefined && otherValue !== '') {
      const numValue = Number(value);
      const numOther = Number(otherValue);
      const isInvalid =
        comparison === 'lessThan'
          ? numValue < numOther
          : comparison === 'greaterThan'
            ? numValue > numOther
            : numValue === numOther;
      if (isInvalid) return errorMessage;
    }

    return false;
  };

export const nonEmptyStringArrayValidator = (
  value: string[] | null | undefined
) => {
  if (value === null || value === undefined) {
    return false;
  }

  const schema = z.array(z.string().min(1, "The value couldn't be empty"));
  const result = schema.safeParse(value);

  if (result.success) return undefined;

  return 'All values must be non-empty strings.';
};
